import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { LoadingPage } from '@/components/ui/loading'
import { MetricCard } from '@/components/ui/metric-card'
import { StatusBadge } from '@/components/ui/status-badge'
import {
  useOverviewStats,
  useRecentAlerts,
  useTopBouncingDomains,
  useVolumeChart,
} from '@/hooks/use-overview'
import { formatNumber, formatPercent, formatUptime } from '@/lib/format'
import type { AlertHistoryEntry, BouncingDomain, VolumeDataPoint } from '@/lib/types'

export function OverviewPage(): React.JSX.Element {
  const { data: stats, isLoading: statsLoading } = useOverviewStats()
  const { data: volumeData, isLoading: chartLoading } = useVolumeChart()
  const { data: alerts, isLoading: alertsLoading } = useRecentAlerts()
  const { data: bouncingDomains, isLoading: domainsLoading } = useTopBouncingDomains()

  if (statsLoading && !stats) {
    return <LoadingPage message="Loading overview..." />
  }

  const engineOffline = stats?.queue_depth === -1

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-text-primary">Overview</h1>

      {/* Top row: 6 metric cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <MetricCard
          label="Emails Sent (today)"
          value={stats ? formatNumber(stats.emails_today) : '--'}
        />
        <MetricCard
          label="Delivery Rate"
          value={stats ? formatPercent(stats.delivery_rate) : '--'}
          variant="success"
        />
        <MetricCard
          label="Bounce Rate"
          value={stats ? formatPercent(stats.bounce_rate) : '--'}
          variant={stats && stats.bounce_rate > 5 ? 'danger' : 'default'}
        />
        <MetricCard
          label="Queue Depth"
          value={engineOffline ? 'Offline' : (stats?.queue_depth.toString() ?? '--')}
          variant={engineOffline ? 'danger' : 'default'}
          sub={engineOffline ? 'Engine unreachable' : undefined}
        />
        <MetricCard
          label="Engine Uptime"
          value={engineOffline ? 'Offline' : stats ? formatUptime(stats.uptime_seconds) : '--'}
          variant={engineOffline ? 'danger' : 'success'}
          sub={engineOffline ? 'Engine unreachable' : undefined}
        />
        <MetricCard label="Active Accounts" value={stats?.active_accounts.toString() ?? '--'} />
      </div>

      {/* Volume chart (24h) */}
      <VolumeChart data={volumeData ?? []} isLoading={chartLoading} />

      {/* Bottom row: alerts + top bouncing domains */}
      <div className="grid gap-6 lg:grid-cols-2">
        <RecentAlerts alerts={alerts ?? []} isLoading={alertsLoading} />
        <TopBouncingDomains domains={bouncingDomains ?? []} isLoading={domainsLoading} />
      </div>
    </div>
  )
}

function VolumeChart({
  data,
  isLoading,
}: {
  data: VolumeDataPoint[]
  isLoading: boolean
}): React.JSX.Element {
  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <div className="mb-4">
        <h2 className="text-sm font-medium text-text-primary">Email Volume (24h)</h2>
        <p className="text-xs text-text-tertiary">Sent / Delivered / Bounced per hour</p>
      </div>
      {isLoading && data.length === 0 ? (
        <div className="flex h-[300px] items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-admin-500 border-t-transparent" />
        </div>
      ) : data.length === 0 ? (
        <div className="flex h-[300px] items-center justify-center">
          <p className="text-sm text-text-tertiary">No volume data available</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-surface-3)" />
            <XAxis dataKey="hour" stroke="var(--color-text-tertiary)" fontSize={11} />
            <YAxis stroke="var(--color-text-tertiary)" fontSize={11} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-surface-0)',
                border: '1px solid var(--color-surface-3)',
                borderRadius: '8px',
                fontSize: '12px',
              }}
            />
            <Area
              type="monotone"
              dataKey="sent"
              stroke="var(--color-admin-500)"
              fill="var(--color-admin-100)"
              strokeWidth={2}
              name="Sent"
            />
            <Area
              type="monotone"
              dataKey="delivered"
              stroke="var(--color-success)"
              fill="var(--color-success)"
              fillOpacity={0.15}
              strokeWidth={2}
              name="Delivered"
            />
            <Area
              type="monotone"
              dataKey="bounced"
              stroke="var(--color-danger)"
              fill="var(--color-danger)"
              fillOpacity={0.15}
              strokeWidth={1.5}
              name="Bounced"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

function mapSeverity(severity: string): string {
  if (severity === 'critical' || severity === 'warning' || severity === 'info') {
    return severity
  }
  return 'info'
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function RecentAlerts({
  alerts,
  isLoading,
}: {
  alerts: AlertHistoryEntry[]
  isLoading: boolean
}): React.JSX.Element {
  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <h2 className="mb-4 text-sm font-medium text-text-primary">Recent Alerts</h2>
      {isLoading && alerts.length === 0 ? (
        <div className="flex h-32 items-center justify-center">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-admin-500 border-t-transparent" />
        </div>
      ) : alerts.length === 0 ? (
        <p className="py-8 text-center text-sm text-text-tertiary">No recent alerts</p>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => (
            <div key={alert.id} className="flex items-start gap-3">
              <StatusBadge status={mapSeverity(alert.severity)} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text-primary">{alert.message}</p>
                <p className="text-xs text-text-tertiary">{timeAgo(alert.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function TopBouncingDomains({
  domains,
  isLoading,
}: {
  domains: BouncingDomain[]
  isLoading: boolean
}): React.JSX.Element {
  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <h2 className="mb-4 text-sm font-medium text-text-primary">Top Bouncing Domains (24h)</h2>
      {isLoading && domains.length === 0 ? (
        <div className="flex h-32 items-center justify-center">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-admin-500 border-t-transparent" />
        </div>
      ) : domains.length === 0 ? (
        <p className="py-8 text-center text-sm text-text-tertiary">No bounce data available</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-3">
                <th className="pb-2 text-left text-xs font-medium uppercase text-text-secondary">
                  Domain
                </th>
                <th className="pb-2 text-right text-xs font-medium uppercase text-text-secondary">
                  Bounce Rate
                </th>
                <th className="pb-2 text-right text-xs font-medium uppercase text-text-secondary">
                  Sent
                </th>
                <th className="pb-2 text-right text-xs font-medium uppercase text-text-secondary">
                  Bounced
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-2">
              {domains.map((d) => (
                <tr key={d.domain}>
                  <td className="py-2.5 font-medium text-text-primary">{d.domain}</td>
                  <td className="py-2.5 text-right">
                    <span
                      className={
                        d.bounceRate > 3 ? 'text-danger font-medium' : 'text-text-secondary'
                      }
                    >
                      {formatPercent(d.bounceRate)}
                    </span>
                  </td>
                  <td className="py-2.5 text-right text-text-secondary">
                    {formatNumber(d.totalSent)}
                  </td>
                  <td className="py-2.5 text-right text-text-secondary">
                    {formatNumber(d.totalBounced)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
